// Utility function for handling image URLs
export const getImageUrl = (
  item?: { imageUrl?: string; image?: string } | string, 
  image?: string, 
  id?: string, 
  imageKey?: number,
  type?: 'character' | 'element' | 'pillar'
) => {
  let imageUrl: string | undefined;
  let imagePath: string | undefined;

  if (typeof item === 'string') {
    imageUrl = item;
    imagePath = image;
  } else if (item && typeof item === 'object') {
    imageUrl = item.imageUrl;
    imagePath = item.image;
  }

  if (imageUrl && imageUrl !== 'undefined') {
    return imageUrl;
  }

  if (imagePath) {
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/')) return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}${imagePath}`;
    if (imagePath.startsWith('characters/')) return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/${imagePath}`;
    if (imagePath.startsWith('elements/')) return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/${imagePath}`;
    if (imagePath.startsWith('pillars/')) return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/${imagePath}`;
    if (imagePath === 'default-element.gif') {
      return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/elements/default-element.gif`;
    }
    // fallback for other types
  }

  // Default images
  if (type === 'character' || (id && id.startsWith('char'))) {
    return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/characters/default-0.jpg`;
  }
  if (type === 'element' || (id && id.startsWith('elem'))) {
    return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/elements/default-element.gif`;
  }
  if (type === 'pillar' || (id && id.startsWith('pill'))) {
    return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/pillars/default-0.jpg`;
  }
  // fallback
  return `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/default-0.jpg`;
};

// Utility function to check if an image is a default image
export const isDefaultImage = (
  item?: { imageUrl?: string; image?: string } | string,
  image?: string,
  type?: 'character' | 'element' | 'pillar'
): boolean => {
  let imageUrl: string | undefined;
  let imagePath: string | undefined;

  if (typeof item === 'string') {
    imageUrl = item;
    imagePath = image;
  } else if (item && typeof item === 'object') {
    imageUrl = item.imageUrl;
    imagePath = item.image;
  }

  // If no image at all, it's not a default image (it's no image)
  if (!imageUrl && !imagePath) {
    return false;
  }

  // Check if the image URL is a default image
  if (imageUrl) {
    const defaultUrls = [
      `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/characters/default-0.jpg`,
      `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/elements/default-element.gif`,
      `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/pillars/default-0.jpg`,
      `${process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1'}/img/default-0.jpg`
    ];
    
    if (defaultUrls.includes(imageUrl)) {
      return true;
    }
  }

  // Check if the image path is a default image
  if (imagePath) {
    const defaultPaths = [
      'characters/default-0.jpg',
      'elements/default-element.gif',
      'pillars/default-0.jpg',
      'default-element.gif',
      'default-0.jpg'
    ];
    
    if (defaultPaths.some(path => imagePath?.includes(path))) {
      return true;
    }
  }

  return false;
};

// Utility function to check if an item has a custom (non-default) image
export const hasCustomImage = (
  item?: { imageUrl?: string; image?: string } | string,
  image?: string,
  type?: 'character' | 'element' | 'pillar'
): boolean => {
  let imageUrl: string | undefined;
  let imagePath: string | undefined;

  if (typeof item === 'string') {
    imageUrl = item;
    imagePath = image;
  } else if (item && typeof item === 'object') {
    imageUrl = item.imageUrl;
    imagePath = item.image;
  }

  // If no image at all, return false
  if (!imageUrl && !imagePath) {
    return false;
  }

  // If it's a default image, return false
  if (isDefaultImage(item, image, type)) {
    return false;
  }

  // If we have an image and it's not default, it's custom
  return true;
};

// Utility function to get Aethermap logo URL for fallback images
export const getAethermapLogoUrl = (): string => {
  return '/img/AeLogo.ico';
};

// Utility function to handle image errors with consistent fallback
export const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
  const img = event.currentTarget;
  if (!img) {
    console.error('❌ Cannot set properties of null (setting src) - img element is null');
    return;
  }
  
  if (img && img.src !== getAethermapLogoUrl() && img.src !== undefined) {
    console.log('🔄 Image failed to load, using Aethermap logo fallback');
    img.src = getAethermapLogoUrl();
    img.style.borderRadius = '15%';
    img.style.width = '50px';
    img.style.height = '50px';
  }
};

export const getBackendUrl = () => {
  return process.env.REACT_APP_API_URL || 'https://aethermap-backend.fly.dev/api/v1';
}; 